import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';

export const fetchInventory = createAsyncThunk(
  'inventory/fetchAll',
  async (
    {
      page = 1,
      limit = 10,
      search = '',
      field = '',
      category = '',
      warehouse_id = '',
      is_active = '',
      date_range = '',
    } = {},
    { rejectWithValue }
  ) => {
    try {
      let url = `/api/v1.0/inventory?page=${page}&limit=${limit}`;
      if (search)      url += `&search=${encodeURIComponent(search)}`;
      if (field)       url += `&field=${encodeURIComponent(field)}`;
      if (category)    url += `&category=${encodeURIComponent(category)}`;
      if (warehouse_id) url += `&warehouse_id=${warehouse_id}`;
      if (is_active !== '') url += `&is_active=${is_active}`;
      if (date_range)  url += `&date_range=${date_range}`;
      const response = await apiPath.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchInventoryLists = createAsyncThunk(
  'inventory/fetchLists',
  async (warehouse_id, { rejectWithValue }) => {
    try {
      const url = warehouse_id
        ? `/api/v1.0/inventory/lists?warehouse_id=${warehouse_id}`
        : '/api/v1.0/inventory/lists';
      const response = await apiPath.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchInventoryById = createAsyncThunk(
  'inventory/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/inventory/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createInventory = createAsyncThunk(
  'inventory/create',
  async (inventoryData, { rejectWithValue }) => {
    try {
      const response = await apiPath.post('/api/v1.0/inventory/create', inventoryData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateInventory = createAsyncThunk(
  'inventory/update',
  async ({ id, inventoryData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/inventory/${id}`, inventoryData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const adjustStock = createAsyncThunk(
  'inventory/adjustStock',
  async ({ id, adjustmentData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.patch(
        `/api/v1.0/inventory/${id}/adjust`,
        adjustmentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteInventory = createAsyncThunk(
  'inventory/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiPath.delete(`/api/v1.0/inventory/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState: {
    inventories:    [],
    inventoryLists: [],
    currentInventory: null,
    loading:        false,
    adjustLoading:  false,
    error:          null,
    totalRecord:    0,
    totalPage:      0,
    currentPage:    1,
    perPage:        10,
  },
  reducers: {
    clearInventoryError: (state) => { state.error = null; },
    setCurrentPage: (state, action) => { state.currentPage = action.payload; },
    clearCurrentInventory: (state) => { state.currentInventory = null; },
  },
  extraReducers: (builder) => {
    builder
      // ── Fetch All ──────────────────────────────────────────────────────────
      .addCase(fetchInventory.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.loading      = false;
        state.inventories  =
          action.payload.records ||
          action.payload.items   ||
          action.payload.inventories || [];
        state.totalRecord  = action.payload.totalRecord  || 0;
        state.totalPage    = action.payload.totalPage    || 0;
        state.currentPage  = action.payload.currentPage  || 1;
        state.perPage      = action.payload.perPage      || 10;
      })
      .addCase(fetchInventory.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Fetch Lists (dropdown) ─────────────────────────────────────────────
      .addCase(fetchInventoryLists.pending,    (state) => { state.loading = true; })
      .addCase(fetchInventoryLists.fulfilled,  (state, action) => {
        state.loading        = false;
        state.inventoryLists =
          action.payload?.data?.items ||
          action.payload?.items ||
          action.payload?.inventories ||
          [];
      })
      .addCase(fetchInventoryLists.rejected,   (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Fetch By ID ────────────────────────────────────────────────────────
      .addCase(fetchInventoryById.pending,    (state) => { state.loading = true; })
      .addCase(fetchInventoryById.fulfilled,  (state, action) => {
        state.loading          = false;
        state.currentInventory = action.payload;
      })
      .addCase(fetchInventoryById.rejected,   (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Create ─────────────────────────────────────────────────────────────
      .addCase(createInventory.pending, (state) => { state.loading = true; })
      .addCase(createInventory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.id) {
          state.inventories  = [action.payload, ...state.inventories];
          state.totalRecord  = state.totalRecord + 1;
        }
      })
      .addCase(createInventory.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Update ─────────────────────────────────────────────────────────────
      .addCase(updateInventory.pending, (state) => { state.loading = true; })
      .addCase(updateInventory.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index   = state.inventories.findIndex((inv) => inv.id === updated.id);
        if (index !== -1) state.inventories[index] = updated;
        if (state.currentInventory?.id === updated.id) state.currentInventory = updated;
      })
      .addCase(updateInventory.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      })

      // ── Adjust Stock ───────────────────────────────────────────────────────
      .addCase(adjustStock.pending, (state) => { state.adjustLoading = true; })
      .addCase(adjustStock.fulfilled, (state, action) => {
        state.adjustLoading = false;
        const updated = action.payload;
        const index   = state.inventories.findIndex((inv) => inv.id === updated.id);
        if (index !== -1) state.inventories[index] = updated;
        if (state.currentInventory?.id === updated.id) state.currentInventory = updated;
      })
      .addCase(adjustStock.rejected, (state, action) => {
        state.adjustLoading = false;
        state.error         = action.payload || action.error.message;
      })

      // ── Delete ─────────────────────────────────────────────────────────────
      .addCase(deleteInventory.pending, (state) => { state.loading = true; })
      .addCase(deleteInventory.fulfilled, (state, action) => {
        state.loading      = false;
        state.inventories  = state.inventories.filter((inv) => inv.id !== action.payload);
        state.totalRecord  = Math.max(0, state.totalRecord - 1);
      })
      .addCase(deleteInventory.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload || action.error.message;
      });
  },
});

export const { clearInventoryError, setCurrentPage, clearCurrentInventory } =
  inventorySlice.actions;
export default inventorySlice.reducer;